import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { regionBySlug } from "@/lib/data/regions";
import { cityBySlug } from "@/lib/data/cities";
import { serviceBySlug } from "@/lib/data/services";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import { ArrowUpRight } from "lucide-react";

export default function RegionDetail() {
  const { slug } = useParams();
  const region = regionBySlug(slug);

  useSEO({
    title: region
      ? `${region.name} Security Coverage — Manitoba Security Services`
      : "Region",
    description: region ? region.hero : undefined,
    keywords: region ? withCanadaKeywords(region.keywords) : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/regions/${slug}`
        : `/regions/${slug}`,
    jsonLd: region
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Manitoba Security Services — ${region.name}`,
          description: region.hero,
          areaServed: {
            "@type": "AdministrativeArea",
            name: `${region.name}, Manitoba`,
            containedInPlace: { "@type": "Country", name: "Canada" },
          },
        }
      : undefined,
  });

  if (!region) return <Navigate to="/regions" replace />;

  const regionCities = (region.cities || []).map((s) => cityBySlug(s)).filter(Boolean);
  const regionServices = (region.services || []).map((s) => serviceBySlug(s)).filter(Boolean);

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-12 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">Region</p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={[region.name]}
            className="font-display font-black uppercase tracking-tightest leading-[0.9] text-white text-5xl md:text-7xl lg:text-8xl"
            delay={0.05}
          />
          <p className="mt-6 font-editorial italic text-[#a0a0a0] text-xl md:text-3xl max-w-3xl">
            {region.tag}
          </p>
          <p className="mt-8 font-body text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
            {region.hero}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-24 space-y-16">
        {region.body.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                {String(i + 1).padStart(2, "0")}
              </p>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {regionCities.length > 0 && (
        <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-16 md:py-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-10">
            <p className="font-editorial italic text-[#FF3B30] text-sm mb-6">
              Cities in {region.name}
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
              {regionCities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/cities/${c.slug}`}
                    data-testid={`region-city-${c.slug}`}
                    className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-3 hover:border-[#FF3B30] transition-colors"
                  >
                    <span className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                      {c.name}
                    </span>
                    <ArrowUpRight className="text-white/40 group-hover:text-[#FF3B30]" size={14} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {regionServices.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-24">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-6">
            Services most relevant to {region.name}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {regionServices.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`region-service-${s.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                >
                  <span className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                    <span className="font-editorial italic text-[#FF3B30] not-italic mr-2">
                      {s.number}
                    </span>
                    {s.name}
                  </span>
                  <ArrowUpRight className="text-white/40 group-hover:text-[#FF3B30]" size={16} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
